import type { Permission, PermissionDef, PermissionGroup, RoleRecord } from '@/types/portal';

/** Every permission definition in the catalog, in matrix order. */
function flattenPermissions(catalog: PermissionGroup[]): PermissionDef[] {
  return catalog.flatMap((group) => group.permissions);
}

/** Every valid permission key — used by "select all" and coverage maths. */
function allPermissionKeys(catalog: PermissionGroup[]): Permission[] {
  return flattenPermissions(catalog).map((permission) => permission.key);
}

export interface RoleCoverage {
  granted: number;
  total: number;
  percent: number;
}

/** How much of the catalog a role can reach. */
export function roleCoverage(role: RoleRecord, catalog: PermissionGroup[]): RoleCoverage {
  const total = allPermissionKeys(catalog).length;
  const granted = role.permissions.length;
  return { granted, total, percent: total > 0 ? Math.round((granted / total) * 100) : 0 };
}

/** Which risk level a role can reach — used to flag powerful roles. */
export function highestRisk(role: RoleRecord, catalog: PermissionGroup[]): PermissionDef['risk'] {
  const granted = new Set(role.permissions);
  const order: PermissionDef['risk'][] = ['Standard', 'Sensitive', 'Critical'];
  let result: PermissionDef['risk'] = 'Standard';
  flattenPermissions(catalog).forEach((permission) => {
    if (granted.has(permission.key) && order.indexOf(permission.risk) > order.indexOf(result)) {
      result = permission.risk;
    }
  });
  return result;
}

export function togglePermission(list: Permission[], key: Permission): Permission[] {
  return list.includes(key) ? list.filter((item) => item !== key) : [...list, key];
}

/** Toggle a whole resource group on or off. */
export function toggleGroup(list: Permission[], keys: Permission[], on: boolean): Permission[] {
  if (on) {
    const merged = new Set([...list, ...keys]);
    return Array.from(merged);
  }
  return list.filter((item) => !keys.includes(item));
}